import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  setDoc,
  query,
  where,
  updateDoc,
} from '@angular/fire/firestore';
import {
  CollectionReference,
  DocumentData,
  limit,
  orderBy,
  startAfter,
  writeBatch,
} from 'firebase/firestore';
import { inject } from '@angular/core';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  ListResult,
} from '@angular/fire/storage';
import { deleteObject } from '@angular/fire/storage';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private firestore: Firestore = inject(Firestore);
  private storage: Storage = inject(Storage);

  async getEmployeeByName(name: string): Promise<any | null> {
    const snapshot = await getDocs(collection(this.firestore, 'Employees'));
    const match = snapshot.docs.find((doc) => doc.data()['name'] === name);
    return match ? { id: match.id, ...match.data() } : null;
  }

  /**
   * Get employee by document ID from Firestore
   * @param employeeId string - The Firestore document ID
   * @returns Promise with employee data or null if not found
   */
  async getEmployeeById(employeeId: string): Promise<any | null> {
    try {
      const employeeRef = doc(this.firestore, 'Employees', employeeId);
      const employeeSnap = await getDoc(employeeRef);

      if (employeeSnap.exists()) {
        return { id: employeeSnap.id, ...employeeSnap.data() };
      } else {
        console.warn(`⚠️ Employee with ID ${employeeId} not found`);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching employee:', error);
      throw error;
    }
  }

  /**
   * Get employee by email
   * @param email string - The employee's email address
   * @returns Promise with employee data or null if not found
   */
  async getEmployeeByEmail(email: string): Promise<any | null> {
    try {
      const employeesRef = collection(this.firestore, 'Employees');
      const q = query(employeesRef, where('email', '==', email.toLowerCase()));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      console.error('❌ Error fetching employee by email:', error);
      throw error;
    }
  }

  /**
   * Get employee by PIN (optionally filtered by orgId)
   * @param pin string - The employee's PIN
   * @param orgId string - Optional organization ID to filter by
   * @returns Promise with employee data or null if not found
   */
  async getEmployeeByPin(pin: string, orgId?: string): Promise<any | null> {
    try {
      const employeesRef = collection(this.firestore, 'Employees');
      let q;

      if (orgId) {
        q = query(
          employeesRef,
          where('Pin', '==', pin),
          where('orgId', '==', orgId),
        );
      } else {
        q = query(employeesRef, where('Pin', '==', pin));
      }

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      return data && typeof data === 'object' ? { id: doc.id, ...data } : null;
    } catch (error) {
      console.error('❌ Error fetching employee by PIN:', error);
      throw error;
    }
  }

  /**
   * Get all employees (admin only)
   * @returns Promise with array of all employees
   */
  async getAllEmployees(): Promise<any[]> {
    try {
      const employeesRef = collection(this.firestore, 'Employees');
      const snapshot = await getDocs(employeesRef);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        cashier: doc.data()['cashier'] ?? false,
      }));
    } catch (error) {
      console.error('❌ Error fetching all employees:', error);
      throw error;
    }
  }

  async addNewRequestOff(data: any): Promise<void> {
    await addDoc(collection(this.firestore, 'requestsOff'), data);
  }

  /**
   * Update employee profile information
   * @param employeeId string - The employee document ID
   * @param data any - The data to update (firstName, lastName, email, Pin, etc.)
   */
  async updateEmployeeProfile(employeeId: string, data: any): Promise<void> {
    const employeeRef = doc(this.firestore, 'Employees', employeeId);
    await updateDoc(employeeRef, data);
  }

  /**
   * Upload a file to Firebase Storage and return the download URL
   * @param path string - Storage path (e.g. 'uploads/profile.png')
   * @param file File - The file to upload
   */
  async uploadFile(path: string, file: File): Promise<string> {
    const fileRef = ref(this.storage, path);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    return url;
  }

  /**
   * Get the download URL for an existing file
   * @param path string - The full path to the file in storage
   */
  async getFileUrl(path: string): Promise<string> {
    const fileRef = ref(this.storage, path);
    return await getDownloadURL(fileRef);
  }

  /**
   * Get all files from a specific folder in Firebase Storage
   * @param folderPath string - The folder path (e.g. 'schedules' or 'schedules/2024')
   * @returns Promise with array of file metadata including name, url, and path
   */
  async getAllFilesFromFolder(folderPath: string): Promise<any[]> {
    const folderRef = ref(this.storage, folderPath);
    const listResult: ListResult = await listAll(folderRef);

    const filesPromises = listResult.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return {
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: url,
        bucket: itemRef.bucket,
      };
    });

    return await Promise.all(filesPromises);
  }

  async getLoginHistory(
    limitCount: number = 50,
    lastDoc: any = null,
  ): Promise<{ logs: any[]; lastDoc: any }> {
    let q = query(
      collection(this.firestore, 'login_history'),
      orderBy('timestamp', 'desc'),
      limit(limitCount),
    );

    if (lastDoc) {
      q = query(
        collection(this.firestore, 'login_history'),
        orderBy('timestamp', 'desc'),
        startAfter(lastDoc),
        limit(limitCount),
      );
    }

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return {
      logs,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    };
  }

  /**
   * Get all files from root of storage bucket
   * @returns Promise with array of file metadata
   */
  async getAllFiles(): Promise<any[]> {
    return this.getAllFilesFromFolder('');
  }

  /**
   * Get all documents from a Firestore collection
   * @param collectionName string - The name of the collection
   * @returns Promise with array of documents
   */
  async getDocuments(collectionName: string): Promise<any[]> {
    const snapshot = await getDocs(collection(this.firestore, collectionName));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  async deleteFile(path: string): Promise<void> {
    const fileRef = ref(this.storage, path);
    await deleteObject(fileRef);
  }

  async addNewScheduleMetadata(data: any): Promise<void> {
    await addDoc(collection(this.firestore, 'schedules'), data);
  }

  /**
   * Fetch schedules filtered by org and sorted by upload date
   * @param orgId string - The organization ID to filter by
   */
  async getSchedulesWithMetadata(orgId: string): Promise<any[]> {
    try {
      if (!orgId) {
        console.warn('⚠️ No organization ID provided');
        return [];
      }

      const schedulesRef = collection(this.firestore, 'schedules');
      const q = query(
        schedulesRef,
        where('orgId', '==', orgId),
        orderBy('uploadedAt', 'desc'),
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (err) {
      console.error('❌ Error fetching schedules:', err);
      throw err;
    }
  }

  // Preferred (when you have docId)
  async updateScheduleTitleById(docId: string, title: string): Promise<void> {
    const ref = doc(this.firestore, 'schedules', docId);
    await updateDoc(ref, { title });
  }

  // Fallback (if you don't have docId)
  async updateScheduleTitleByFileName(
    fileName: string,
    title: string,
  ): Promise<void> {
    const q = query(
      collection(this.firestore, 'schedules'),
      where('fileName', '==', fileName),
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map((d) => updateDoc(d.ref, { title })));
  }

  /** 🔹 Get organization details by orgId */
  async getOrganizationById(orgId: string): Promise<any | null> {
    try {
      const orgRef = doc(this.firestore, 'organizations', orgId);
      const orgSnap = await getDoc(orgRef);
      return orgSnap.exists() ? { id: orgSnap.id, ...orgSnap.data() } : null;
    } catch (err) {
      console.error('Error fetching organization:', err);
      return null;
    }
  }

  /** 🔹 Get all employees in an organization */
  async getEmployeesByOrgId(orgId: string): Promise<any[]> {
    try {
      const empRef = collection(this.firestore, 'Employees');
      const q = query(empRef, where('orgId', '==', orgId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        cashier: d.data()['cashier'] ?? false,
      }));
    } catch (err) {
      console.error('Error fetching employees by orgId:', err);
      return [];
    }
  }

  /**
   * Get all brands from Firestore
   * @returns Promise with array of brands
   */
  async getAllBrands(): Promise<any[]> {
    try {
      const brandsRef = collection(this.firestore, 'brands');
      const snapshot = await getDocs(brandsRef);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('❌ Error fetching brands:', error);
      throw error;
    }
  }

  /**
   * Get all organizations from Firestore
   * @returns Promise with array of organizations
   */
  async getAllOrganizations(): Promise<any[]> {
    try {
      const orgsRef = collection(this.firestore, 'organizations');
      const snapshot = await getDocs(orgsRef);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('❌ Error fetching organizations:', error);
      throw error;
    }
  }

  /**
   * Create a new brand
   * @param brandData - The brand data (name, active)
   * @returns Promise with the created brand ID
   */
  async createBrand(brandData: any): Promise<string> {
    try {
      const brandsRef = collection(this.firestore, 'brands');
      const docRef = await addDoc(brandsRef, {
        ...brandData,
        createdAt: new Date(),
      });
      console.log('✅ Brand created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating brand:', error);
      throw error;
    }
  }

  /**
   * Create a new organization
   * @param orgData - The organization data (name, brandId, location, active)
   * @returns Promise with the created organization ID
   */
  async createOrganization(orgData: any): Promise<string> {
    try {
      // Generate a unique orgId based on name
      const orgId =
        orgData.name
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
          .substring(0, 20) + Math.floor(Math.random() * 1000);

      const orgsRef = collection(this.firestore, 'organizations');
      const docRef = await addDoc(orgsRef, {
        ...orgData,
        orgId: orgId,
        createdAt: new Date(),
      });

      console.log('✅ Organization created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating organization:', error);
      throw error;
    }
  }

  /**
   * Create a new employee (manager)
   * @param employeeData - The employee/manager data
   * @returns Promise with the created employee ID
   */
  async createEmployee(employeeData: any): Promise<string> {
    try {
      const employeesRef = collection(this.firestore, 'Employees');
      const docRef = await addDoc(employeesRef, {
        ...employeeData,
        cashier: employeeData?.cashier ?? false,
        createdAt: new Date(),
      });

      console.log('✅ Employee created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating employee:', error);
      throw error;
    }
  }

  /**
   * Create a new support ticket with optional image upload
   * @param ticketData - Object containing page, description, etc.
   * @param imageFile - Optional file to upload
   */
  async createSupportTicket(ticketData: any, imageFile?: File): Promise<void> {
    try {
      let imageUrl = null;

      // 1️⃣ Upload image if provided
      if (imageFile) {
        const fileName = `${Date.now()}_${imageFile.name}`;
        const filePath = `tickets/${fileName}`;
        imageUrl = await this.uploadFile(filePath, imageFile);
      }

      // 2️⃣ Add document to Firestore
      const ticketsRef = collection(this.firestore, 'tickets');
      await addDoc(ticketsRef, {
        ...ticketData,
        imageUrl: imageUrl,
        createdAt: new Date(),
      });

      console.log('✅ Ticket successfully created');
    } catch (error) {
      console.error('❌ Error creating support ticket:', error);
      throw error;
    }
  }

  /**
   * Update shifts in a published schedule after a swap is approved
   * @param scheduleId - The schedule document ID (e.g., "platoscloset555-published-11-09-11-15")
   * @param employee1Name - First employee's name
   * @param employee2Name - Second employee's name
   * @param dayKey - The day key (e.g., "Mon", "Tue", etc.)
   */
  async swapShiftsInPublishedSchedule(
    scheduleId: string,
    employee1Name: string,
    employee2Name: string,
    dayKey: string,
  ): Promise<void> {
    try {
      const scheduleRef = doc(this.firestore, 'schedules', scheduleId);
      const scheduleSnap = await getDoc(scheduleRef);

      if (!scheduleSnap.exists()) {
        throw new Error('Schedule not found');
      }

      const scheduleData = scheduleSnap.data();
      const result = scheduleData['result'] || [];

      // Find both employees in the result array
      const employee1Index = result.findIndex(
        (emp: any) => emp.name?.trim() === employee1Name.trim(),
      );
      const employee2Index = result.findIndex(
        (emp: any) => emp.name?.trim() === employee2Name.trim(),
      );

      if (employee1Index === -1 || employee2Index === -1) {
        throw new Error('One or both employees not found in schedule');
      }

      // Get the shifts
      const employee1Shift = result[employee1Index][dayKey];
      const employee2Shift = result[employee2Index][dayKey];

      // Swap the shifts
      result[employee1Index][dayKey] = employee2Shift;
      result[employee2Index][dayKey] = employee1Shift;

      // Update the schedule in Firestore
      await updateDoc(scheduleRef, {
        result: result,
        lastModified: new Date(),
        lastModifiedBy: 'System - Shift Swap',
      });

      console.log(
        `✅ Swapped shifts for ${employee1Name} and ${employee2Name} on ${dayKey}`,
      );
    } catch (error) {
      console.error('❌ Error swapping shifts in schedule:', error);
      throw error;
    }
  }

  // ADD THESE FUNCTIONS TO YOUR EXISTING firebase.service.ts

  /**
   * Get count of shift swap requests awaiting employee approval
   * @param orgId - The organization ID
   * @param employeeName - The employee's name
   * @returns Promise with count of pending employee approvals
   */
  async getEmployeePendingSwapCount(
    orgId: string,
    employeeName: string,
  ): Promise<number> {
    try {
      const swapRef = collection(this.firestore, 'shiftSwapRequests');
      const q = query(
        swapRef,
        where('orgId', '==', orgId),
        where('shift2.employeeName', '==', employeeName),
        where('status', '==', 'pending_employee'),
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('❌ Error fetching employee pending swap count:', error);
      return 0;
    }
  }

  /**
   * Get count of shift swap requests awaiting manager approval
   * @param orgId - The organization ID
   * @returns Promise with count of pending manager approvals
   */
  async getManagerPendingSwapCount(orgId: string): Promise<number> {
    try {
      const swapRef = collection(this.firestore, 'shiftSwapRequests');
      const q = query(
        swapRef,
        where('orgId', '==', orgId),
        where('status', '==', 'pending_manager'),
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('❌ Error fetching manager pending swap count:', error);
      return 0;
    }
  }

  /**
   * Get total count of shift swap requests awaiting approval (employee OR manager)
   * @param orgId - The organization ID
   * @param employeeName - The employee's name
   * @param isManager - Whether the user is a manager
   * @returns Promise with total count of pending approvals
   */
  async getTotalPendingSwapCount(
    orgId: string,
    employeeName: string,
    isManager: boolean = false,
  ): Promise<number> {
    try {
      let totalCount = 0;

      // Get employee pending count (requests where this employee needs to approve)
      const employeeCount = await this.getEmployeePendingSwapCount(
        orgId,
        employeeName,
      );
      totalCount += employeeCount;

      // If manager, also get manager pending count
      if (isManager) {
        const managerCount = await this.getManagerPendingSwapCount(orgId);
        totalCount += managerCount;
      }

      return totalCount;
    } catch (error) {
      console.error('❌ Error fetching total pending swap count:', error);
      return 0;
    }
  }

  /**
   * Get organization name by orgId
   * @param orgId - The organization ID (e.g., "platoscloset555")
   * @returns Promise with organization name or null if not found
   */
  async getOrgNameByOrgId(orgId: string): Promise<string | null> {
    try {
      if (!orgId) {
        console.warn('⚠️ No orgId provided');
        return null;
      }

      const orgsRef = collection(this.firestore, 'organizations');

      // Try querying by orgId field first
      let q = query(orgsRef, where('orgId', '==', orgId));
      let snapshot = await getDocs(q);

      // If not found, try using the document ID directly
      if (snapshot.empty) {
        console.log('🔍 Trying to fetch by document ID:', orgId);
        const orgDoc = doc(this.firestore, 'organizations', orgId);
        const orgSnap = await getDoc(orgDoc);

        if (orgSnap.exists()) {
          const data = orgSnap.data();
          console.log('✅ Found organization by doc ID:', data);
          return data['name'] || data['orgName'] || null;
        }
      } else {
        const orgData = snapshot.docs[0].data();
        console.log('✅ Found organization by orgId query:', orgData);
        return orgData['name'] || orgData['orgName'] || null;
      }

      console.warn(`⚠️ No organization found with orgId: ${orgId}`);
      return null;
    } catch (error) {
      console.error('❌ Error fetching organization name:', error);
      return null;
    }
  }

  /**
   * Cleans up expired requested-off dates for all employees in an organization
   *
   * Uses individual updates instead of batch to handle errors gracefully
   */
  async cleanupExpiredRequestedOff(
    orgId: string,
  ): Promise<{ cleaned: number; total: number; errors: number }> {
    // Using 'Employees' with capital E
    const employeesRef = collection(this.firestore, 'Employees');
    const q = query(employeesRef, where('orgId', '==', orgId));
    const snapshot = await getDocs(q);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    console.log("🔍 Today's date:", today.toLocaleDateString());
    console.log(`🔍 Checking ${snapshot.size} employees in org: ${orgId}`);

    let cleanedCount = 0;
    let errorCount = 0;
    const totalEmployees = snapshot.size;

    // Process each employee individually instead of batch
    for (const docSnap of snapshot.docs) {
      const employee = docSnap.data();
      const requestedOff = employee['requestedOff'] || [];

      console.log(`\n👤 ${employee['Name'] || 'Unknown'} (${docSnap.id})`);

      if (requestedOff.length > 0) {
        console.log(`   Found ${requestedOff.length} requested-off dates`);

        // Filter out past dates
        const filteredRequests = requestedOff.filter((request: any) => {
          const requestDate = new Date(request.date);
          const isFuture = requestDate >= today;
          console.log(
            `   📅 ${request.date} → ${isFuture ? 'KEEP' : 'REMOVE'}`,
          );
          return isFuture;
        });

        const removedCount = requestedOff.length - filteredRequests.length;

        // Only update if we removed something
        if (removedCount > 0) {
          console.log(`   🗑️  Removing ${removedCount} past dates...`);

          try {
            // Update this employee individually
            const employeeRef = doc(this.firestore, 'Employees', docSnap.id);
            await updateDoc(employeeRef, { requestedOff: filteredRequests });

            cleanedCount++;
            console.log(`   ✅ Updated successfully!`);
          } catch (error: any) {
            errorCount++;
            console.error(
              `   ❌ Error updating ${employee['Name']}:`,
              error.message,
            );
            console.error(`   Document ID: ${docSnap.id}`);
            console.error(`   Full error:`, error);
          }
        } else {
          console.log(`   ⏭️  All dates are future - no cleanup needed`);
        }
      } else {
        console.log(`   ⏭️  No requested-off dates`);
      }
    }

    console.log(`\n📊 Summary:`);
    console.log(`   Total employees: ${totalEmployees}`);
    console.log(`   Successfully cleaned: ${cleanedCount}`);
    console.log(`   Errors: ${errorCount}`);

    return { cleaned: cleanedCount, total: totalEmployees, errors: errorCount };
  }

  /**
   * One-time utility: backfill missing cashier field for an org.
   */
  async backfillMissingCashierForOrg(
    orgId: string,
  ): Promise<{ updated: number; skipped: number; total: number }> {
    const employeesRef = collection(this.firestore, 'Employees');
    const q = query(employeesRef, where('orgId', '==', orgId));
    const snapshot = await getDocs(q);

    let updated = 0;
    let skipped = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const hasCashierField = Object.prototype.hasOwnProperty.call(
        data,
        'cashier',
      );

      if (hasCashierField) {
        skipped++;
        continue;
      }

      const employeeRef = doc(this.firestore, 'Employees', docSnap.id);
      await updateDoc(employeeRef, { cashier: false });
      updated++;
    }

    return { updated, skipped, total: snapshot.size };
  }
}
